import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

const Button = styled.div(({ theme }) => {
  const { color, shadow, type } = theme;

  return `
    position: relative;
    background-color: ${color.grayscale[1000]};
    border-radius: 16px;
    box-shadow: ${shadow.regular};
    padding: 24px 1rem;
    font-size: ${type.size.small};
    display: flex;
    flex-flow: column nowrap;
    justify-content: flex-start;
    align-items: center;
    font-weight: ${type.weight.bold};
    line-height: 1.375;
    cursor: pointer;

    @media (prefers-reduced-motion: no-preference) {
      transition: all 150ms ease-in-out;
    }

    .animated {
      display: none;
    }

    .static {
      display: block;
    }

    &:hover:not(:active) {
      box-shadow: ${shadow.hover};
      transform: scale(1.025, 1.025);

      .animated {
        display: block;
      }

      .static {
        display: none;
      }
    }

    &:hover:active {
      box-shadow: ${shadow.active};
      transform: scale (1.0125, 1.0125);

      .animated {
        display: block;
      }

      .static {
        display: none;
      }
      
      @media (prefers-reduced-motion: no-preference) {
        transition: all 25ms ease-in-out;
      }
    }
  `;
});

const SkillIcon = styled.img`
  height: 80px;
  width: 80px;
`;

const IconWrapper = styled.div`
  flex-shrink: 1;
  min-height: 80px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  vertical-align: middle;
  line-height: 1.5;
  flex-grow: 1;
  min-height: 3rem;
  text-align: center;
  position: relative;
  padding-right: 1.2rem;
`;

const StyledChevron = styled(FontAwesomeIcon)`
  height: 1rem;
  display: inline-block;
  position: absolute;
  right: 0;
  top: 54%;
  transform: translateY(-50%);
  //height: 16px;
  width: 6px;
`;

export interface ISkillsButton {
  title: string;
  staticIcon?: string;
  dynamicIcon?: string;
  callback: () => Promise<void> | void;
  hideChevron?: boolean;
  skillId?: number;
  animate: boolean;
}

const SkillsButton: FC<ISkillsButton> = ({ title, staticIcon, dynamicIcon, callback, hideChevron, animate }) => {
  const [iconPath, setIconPath] = useState<string>();
  const [animatedIconPath, setAnimatedIconPath] = useState<string>();

  const clickHandler = () => {
    callback();
  };

  useEffect(() => {
    if (staticIcon) {
      setIconPath(staticIcon);
    } else if (dynamicIcon) {
      const icon = new URL(`../../assets/icons/functional-skills/${dynamicIcon}`, import.meta.url).href;

      setIconPath(icon);

      if (animate) {
        const animatedIcon = new URL(`../../assets/icons/functional-skills/animated/${dynamicIcon}`, import.meta.url)
          .href;

        setAnimatedIconPath(animatedIcon);
      }
    }
  }, [dynamicIcon, staticIcon, animate]);

  return (
    <Button onClick={clickHandler} role="button">
      <IconWrapper>
        {iconPath && <SkillIcon src={iconPath} alt="" className={animatedIconPath ? 'static' : ''} />}
        {animatedIconPath && <SkillIcon src={animatedIconPath} alt="" className="animated" />}
      </IconWrapper>

      <TextWrapper>
        <span>
          {title} {!hideChevron && <StyledChevron icon={faChevronRight} />}
        </span>
      </TextWrapper>
    </Button>
  );
};

export default SkillsButton;
